/**
 * Motor de voz/vídeo/ecrã — a razão de existir do produto.
 *
 * O LiveKit trata do SFU e do WebRTC; isto é a camada fina por cima que a UI
 * consome. Três decisões que valem a pena explicar:
 *
 *  1. VIVE FORA DO REACT. Uma `Room` é um recurso com sockets e tracks, não
 *     estado renderizável. Guardá-la num `useState` fá-la ser recriada a cada
 *     StrictMode-remount e deixa PeerConnections órfãs a comer CPU.
 *  2. SNAPSHOT IMUTÁVEL + `useSyncExternalStore`. O SDK emite dezenas de
 *     eventos por segundo (níveis de áudio); recalcular um snapshot só quando
 *     algo VISÍVEL muda é o que evita re-render a 30fps na app inteira.
 *  3. O ÁUDIO É ANEXADO À MÃO. O SDK cru não põe áudio no DOM — quem não anexa
 *     fica com uma chamada "ligada" e muda. O Safari exige o elemento no DOM,
 *     por isso há um contentor escondido.
 */

import {
  type ConnectionQuality,
  ConnectionState,
  type LocalAudioTrack,
  type Participant,
  RemoteParticipant,
  Room,
  RoomEvent,
  Track,
  type TrackPublication,
} from "livekit-client";

import { isSecureMediaContext } from "./host";
import { INTERVALO_MS, lerRede, type NetSample } from "./netStats";
import { playSound } from "./sounds";
import { readUserVolume, writeUserVolume } from "./userVolumes";

export type VoiceConnState = "idle" | "connecting" | "connected" | "reconnecting" | "failed";

/** Tópico do canal de dados do LiveKit por onde viaja o quadro branco (e o que
 *  mais precisar de broadcast leve na call). Filtrar por tópico deixa-nos somar
 *  outros usos sem que um leia as mensagens do outro. */
const DATA_TOPIC = "gv-data";

const enc = new TextEncoder();
const dec = new TextDecoder();

/**
 * A mensagem que explica o único bloqueio que NÃO é do nosso código: sobre
 * `http://<ip>` o browser esconde `navigator.mediaDevices`, por isso ecrã,
 * câmara e microfone são impossíveis — não é bug, é a regra de contexto seguro.
 */
const INSECURE_CONTEXT_MSG =
  "Para compartilhar tela, câmera ou microfone você precisa de uma conexão segura: " +
  "abra o app em https:// ou em http://localhost. Neste endereço o browser " +
  "bloqueia a captura de mídia (veja a seção Rede/HTTPS do README).";

/** Um track que sabe responder pelo estado da ligação. */
interface ComEstatisticas {
  getRTCStatsReport(): Promise<RTCStatsReport | undefined>;
}

/** O ponto de partida quando não há relatório nenhum para ler. */
const SEM_MEDIDA: NetSample = { rttMs: null, lossPct: null, at: 0 };

/**
 * O RTT do ping/pong da SINALIZAÇÃO, que o SDK já mede e guarda.
 *
 * `engine.client` está marcado `@internal` no `livekit-client`: é público em
 * TypeScript, mas não é contrato — uma versão nova pode arrumá-lo noutro sítio
 * sem avisar. Daí a leitura desconfiada (existe? é número? é positivo?) e daí
 * ser reserva e não fonte principal: se um dia desaparecer, o mostrador perde
 * o número no caso sem tracks e não parte nada.
 */
function rttDeSinal(room: Room): number | null {
  const bruto = (room as unknown as { engine?: { client?: { rtt?: unknown } } }).engine?.client?.rtt;
  if (typeof bruto !== "number" || !Number.isFinite(bruto) || bruto <= 0) return null;
  return Math.round(bruto);
}

function temStats(track: unknown): track is ComEstatisticas {
  return typeof (track as ComEstatisticas | null | undefined)?.getRTCStatsReport === "function";
}

export interface ParticipantView {
  /** É o `userId` do GreenVoice: o token é emitido com `identity = userId`. */
  identity: string;
  isLocal: boolean;
  speaking: boolean;
  micEnabled: boolean;
  quality: ConnectionQuality;
  camera: Track | null;
  screen: Track | null;
}

export interface VoiceSnapshot {
  state: VoiceConnState;
  error: string | null;
  participants: ParticipantView[];
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenEnabled: boolean;
  /** Há áudio a acompanhar a MINHA partilha de ecrã — só aparece se o utilizador
   *  marcou "partilhar som do separador" no diálogo do browser. Sem isto não faz
   *  sentido oferecer o botão de silenciar (não há nada para silenciar). */
  screenAudioAvailable: boolean;
  /** O som da minha partilha está silenciado para os outros: o track continua
   *  publicado mas mudo, portanto ninguém o ouve. O vídeo do ecrã não é afectado. */
  screenAudioMuted: boolean;
  deafened: boolean;
  /** O browser bloqueou o autoplay: é preciso um clique para ouvir. */
  needsAudioUnlock: boolean;
  /** Identidades (= userId) com a mão no ar, EU incluído se a levantar. Estado
   *  persistente da call — sobrevive a `refresh()`, só se limpa ao sair. */
  raisedHands: string[];
  /** A pessoa a quem estou a falar EM PRIVADO: o meu microfone chega só a ela.
   *  `null` = falo para a sala toda, que é o normal. */
  privateTo: string | null;
  /** Quem está a falar em privado PARA MIM. Vem pelo canal de dados e não do
   *  SFU: do meu lado não há maneira nenhuma de saber que o áudio que ouço foi
   *  endereçado só a mim — só quem restringe é que sabe. */
  privateFrom: string[];
}

const IDLE: VoiceSnapshot = {
  state: "idle",
  error: null,
  participants: [],
  micEnabled: false,
  cameraEnabled: false,
  screenEnabled: false,
  screenAudioAvailable: false,
  screenAudioMuted: false,
  deafened: false,
  needsAudioUnlock: false,
  raisedHands: [],
  privateTo: null,
  privateFrom: [],
};

export interface ConnectOptions {
  url: string;
  token: string;
  micEnabled: boolean;
  deafened: boolean;
}

/**
 * Quanto tempo se aceita ver "A ligar…" antes de o motor admitir que não vai lá.
 *
 * "A ligar…" é o único estado do painel sem fim natural: enquanto ninguém disser
 * o contrário, ele fica. E a 2026-09-02 ficou sete horas — o SFU anunciava um
 * endereço de uma rede que já tinha desaparecido, a sinalização passava pelo
 * túnel na mesma, portanto o painel abria e ficava a girar sem que houvesse
 * jamais alguém a quem ligar.
 *
 * Acima dos 15s+15s que o próprio LiveKit espera (sinalização + ICE): o erro
 * DELE sabe dizer o que falhou e tem sempre de ganhar a este. Isto aqui é a rede
 * por baixo — para o caso de o `connect` (ou o `getUserMedia` que ele chama, que
 * fica pendurado enquanto a pergunta de permissão estiver por responder) nunca
 * responder de todo.
 */
const MS_LIMITE_DE_LIGACAO = 35_000;

class VoiceEngine {
  /**
   * "Estou a falar?" — medido AQUI, no meu próprio microfone.
   *
   * Para os outros, quem responde é o SFU: ele recebe o áudio de toda a gente,
   * mede o nível e avisa quem está na sala. Para MIM isso é uma ida ao servidor
   * para saber uma coisa que já está nesta máquina — e uma ida que pode não
   * voltar: o nível chega por uma extensão do cabeçalho RTP, passa por um limiar
   * fixo do lado de lá (`active_level`, em `infra/livekit.yaml`) e pelo DTX, que
   * corta a transmissão em silêncio. Um microfone de portátil, baixinho, pode
   * nunca cruzar esse limiar: a pessoa fala, é ouvida, e o anel nunca acende.
   *
   * O analisador do LiveKit lê o `MediaStreamTrack` ANTES de tudo isso — antes
   * do codec, do DTX e da rede. Se há voz no microfone, isto sabe.
   */
  private analisador: Analisador | null = null;
  private relogioDaVoz: number | null = null;
  private escuta: Escuta = ESCUTA_INICIAL;

  private room: Room | undefined;
  private snapshot: VoiceSnapshot = IDLE;
  /** Conta as tentativas de ligação. Uma tentativa que já não é a actual (saiu-se
   *  da call, ou entrou-se noutra sala entretanto) NÃO pode falar pelo motor: sem
   *  este número, uma ligação velha a rejeitar tarde chamava `disconnect()` e
   *  desligava a sala BOA — a call continuava a ouvir-se e os controlos ficavam
   *  todos inertes, por baixo de uma etiqueta verde a dizer "Ligado". */
  private generation = 0;
  private readonly listeners = new Set<() => void>();
  private audioSink: HTMLDivElement | undefined;
  private readonly attached = new Map<string, HTMLMediaElement>();
  /** Ouvintes do canal de dados (quadro branco). Sobrevivem a disconnect: a UI
   *  subscreve uma vez à montagem, não a cada ligação. */
  private readonly dataListeners = new Set<(from: string, data: unknown) => void>();
  /** Ouvintes de reacções (emojis a flutuar). Efémeras: não entram no snapshot,
   *  disparam um efeito visual e desaparecem — daí um canal próprio. */
  private readonly reactionListeners = new Set<(from: string, emoji: string) => void>();

  /**
   * O ping de cada um, por identidade — o meu medido aqui, o dos outros
   * anunciado por eles (ver `netStats.ts`: o RTT de uma máquina só existe
   * nessa máquina).
   *
   * FORA do snapshot, e a razão é a mesma do nível de voz logo acima: o
   * snapshot leva os participantes, os tracks e o estado da call, e o palco
   * inteiro re-renderiza quando ele muda. Um número que se actualiza a cada
   * cinco segundos não tem nada que arrastar vídeo atrás de si — quem quer
   * pings subscreve isto, e é só o mostrador que se volta a desenhar.
   */
  private net: Readonly<Record<string, NetSample>> = {};
  private readonly netListeners = new Set<() => void>();
  private relogioDaRede: number | null = null;

  // ── ligação ao React ────────────────────────────────────────

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): VoiceSnapshot => this.snapshot;

  /** O mesmo contrato do `subscribe`, para o mostrador da ligação. Canal
   *  próprio: quem só quer o ping não acorda com cada evento da call. */
  subscribeNet = (listener: () => void): (() => void) => {
    this.netListeners.add(listener);
    return () => this.netListeners.delete(listener);
  };

  /** O objecto é SUBSTITUÍDO a cada mudança, nunca mutado: o
   *  `useSyncExternalStore` compara referências, e mutar por dentro dava um
   *  mostrador que só se actualizava quando outra coisa qualquer o obrigasse.
   */
  getNet = (): Readonly<Record<string, NetSample>> => this.net;

  /** Identidade local na call (= userId). `null` fora de uma ligação — quem
   *  desenha no quadro precisa dela para marcar a autoria das operações. */
  /**
   * O nível do MEU microfone agora, de 0 a 1 — para desenhar um medidor.
   *
   * Não entra no snapshot de propósito. Um número que muda dez vezes por segundo
   * dentro do estado que o React observa é o palco inteiro a re-renderizar a
   * 10Hz, com vídeo lá dentro. Quem desenha o medidor lê isto num intervalo
   * próprio e escreve direito no DOM; o React nunca sabe que existe.
   *
   * Zero quando não há microfone a medir — que é também a resposta certa para
   * "estou mudo": nada a entrar.
   */
  nivelDeVoz(): number {
    return this.analisador?.volume() ?? 0;
  }

  get localId(): string | null {
    return this.room?.localParticipant.identity ?? null;
  }

  // ── canal de dados (quadro branco) ──────────────────────────

  /** Subscreve mensagens do canal de dados. Devolve a função de cancelar.
   *  Independente do ciclo de vida da sala — não é limpo no disconnect. */
  subscribeData = (listener: (from: string, data: unknown) => void): (() => void) => {
    this.dataListeners.add(listener);
    return () => this.dataListeners.delete(listener);
  };

  /**
   * Difunde um objeto por todos na call via canal de dados do LiveKit.
   * `reliable` para operações que não podem perder-se (novo traço, limpar);
   * `false` (lossy, mais rápido) para o fluxo de pontos durante o arrasto.
   */
  sendData(data: unknown, reliable = true): void {
    const room = this.room;
    if (!room) return;
    try {
      room.localParticipant.publishData(enc.encode(JSON.stringify(data)), {
        reliable,
        topic: DATA_TOPIC,
      });
    } catch {
      // Um pacote perdido no quadro não justifica rebentar a call.
    }
  }

  // ── reacções + mão no ar (mesmo transporte do quadro) ───────

  /** Subscreve reacções recebidas (`from`, `emoji`). Devolve o cancelar.
   *  Independente da sala, tal como o canal de dados. */
  subscribeReaction = (listener: (from: string, emoji: string) => void): (() => void) => {
    this.reactionListeners.add(listener);
    return () => this.reactionListeners.delete(listener);
  };

  /** Manda uma reacção a todos — e mostra-a já a mim próprio, sem esperar o eco
   *  do servidor (o meu emoji flutua no mesmo instante em que carrego). */
  sendReaction(emoji: string): void {
    if (!emoji) return;
    this.emitReaction(this.localId ?? "", emoji);
    this.sendData({ k: "react", emoji });
  }

  private emitReaction(from: string, emoji: string): void {
    if (!emoji) return;
    for (const listener of this.reactionListeners) listener(from, emoji);
  }

  /** Levanta/baixa a MINHA mão e anuncia-o. Idempotente: baixar já baixada
   *  não faz nada. Quem entra depois recebe o reanúncio em `ParticipantConnected`. */
  raiseHand(up: boolean): void {
    const me = this.localId;
    if (!me) return;
    this.setHand(me, up);
    this.sendData({ k: "hand", up });
  }

  private setHand(identity: string, up: boolean): void {
    if (!identity) return;
    const has = this.snapshot.raisedHands.includes(identity);
    if (up === has) return;
    const raisedHands = up
      ? [...this.snapshot.raisedHands, identity]
      : this.snapshot.raisedHands.filter((h) => h !== identity);
    this.emit({ raisedHands });
  }

  // ── voz privada ─────────────────────────────────────────────

  /**
   * Fala só para UMA pessoa da call. `null` volta a falar para a sala.
   *
   * Quem corta é o SFU, não o browser: `setTrackSubscriptionPermissions` diz ao
   * servidor quem tem direito a subscrever que traço meu. É a única forma
   * honesta de o fazer — silenciar do lado de quem ouve seria um pedido de
   * favor a um cliente que pode não o cumprir, e a voz saía na mesma pelo fio.
   *
   * O que NÃO se faz aqui é o mais importante: um `allParticipantsAllowed:
   * false` seco cortava TUDO, e a partilha de ecrã que está a decorrer
   * desaparecia do ecrã de toda a gente no instante em que se carregasse em
   * "voz privada" — um botão de voz não pode desligar a apresentação. Por isso
   * cada um dos outros continua com autorização EXPLÍCITA para os meus traços
   * que não são o microfone; o alvo recebe `allowAll` e é o único que ouve.
   *
   * O microfone em si não se toca: entrar em modo privado não abre o micro a
   * quem o tinha fechado. Abrir o microfone é sempre um gesto, nunca um efeito
   * secundário de outro botão.
   */
  async setPrivateVoice(identity: string | null): Promise<void> {
    const room = this.room;
    if (!room) return;
    const target = identity && identity !== this.localId ? identity : null;
    const previous = this.snapshot.privateTo;
    if (target === previous) return;

    this.emit({ privateTo: target });
    // Avisa-se SEMPRE quem sai do modo — mesmo que se troque directamente de
    // pessoa. Sem isto, a primeira ficava com o aviso "estão a falar-te" preso
    // no ecrã para o resto da call.
    if (previous) this.sendTo(previous, { k: "priv", on: false });
    if (target) this.sendTo(target, { k: "priv", on: true });
    await this.applyPrivateVoice();
  }

  /**
   * Empurra a regra para o SFU. Chamada também a cada entrada de alguém e a cada
   * traço novo meu: as permissões são uma FOTOGRAFIA da sala no momento em que
   * são enviadas — quem entra depois não consta da lista e, com o "todos" já
   * desligado, não receberia nada meu. Reaplicar é o que mantém a promessa.
   */
  private async applyPrivateVoice(): Promise<void> {
    const room = this.room;
    if (!room) return;
    const local = room.localParticipant;
    const target = this.snapshot.privateTo;

    if (!target) {
      local.setTrackSubscriptionPermissions(true);
      return;
    }

    // Tudo o que não é o microfone continua a chegar a toda a gente.
    const publicSids: string[] = [];
    for (const publication of local.trackPublications.values() as Iterable<TrackPublication>) {
      if (publication.source === Track.Source.Microphone) continue;
      if (publication.trackSid) publicSids.push(publication.trackSid);
    }

    const permissions = [...room.remoteParticipants.values()].map((participant) =>
      participant.identity === target
        ? { participantIdentity: participant.identity, allowAll: true }
        : { participantIdentity: participant.identity, allowAll: false, allowedTrackSids: publicSids },
    );
    local.setTrackSubscriptionPermissions(false, permissions);
  }

  /** Manda um objecto a UMA pessoa. O canal de dados aceita destinatários, e um
   *  aviso de voz privada difundido à sala toda deixava de ser privado. */
  private sendTo(identity: string, data: unknown): void {
    const room = this.room;
    if (!room) return;
    try {
      room.localParticipant.publishData(enc.encode(JSON.stringify(data)), {
        reliable: true,
        topic: DATA_TOPIC,
        destinationIdentities: [identity],
      });
    } catch {
      // Mesmo critério do `sendData`: um aviso perdido não derruba a call.
    }
  }

  private setPrivateFrom(identity: string, on: boolean): void {
    if (!identity) return;
    const has = this.snapshot.privateFrom.includes(identity);
    if (on === has) return;
    this.emit({
      privateFrom: on
        ? [...this.snapshot.privateFrom, identity]
        : this.snapshot.privateFrom.filter((p) => p !== identity),
    });
  }

  // ── dispositivos (micro / câmara / altifalante) ─────────────

  /** Lista os dispositivos de um tipo. Abstrai `enumerateDevices`; devolve `[]`
   *  em vez de rebentar se o browser recusar (sem permissões ainda). */
  async listDevices(kind: MediaDeviceKind): Promise<MediaDeviceInfo[]> {
    try {
      return await Room.getLocalDevices(kind);
    } catch {
      return [];
    }
  }

  /** deviceId activo de um tipo, para marcar o seleccionado no menu. */
  getActiveDevice(kind: MediaDeviceKind): string | undefined {
    return this.room?.getActiveDevice(kind);
  }

  /** Troca o dispositivo activo. O SFU re-publica o track sem cair a call. */
  async setDevice(kind: MediaDeviceKind, deviceId: string): Promise<void> {
    if (!this.room) return;
    try {
      await this.room.switchActiveDevice(kind, deviceId);
      this.emit({ error: null });
    } catch (error) {
      this.emit({ error: describe(error) });
    }
    this.refresh();
  }

  private emit(patch: Partial<VoiceSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  // ── ciclo de vida ───────────────────────────────────────────

  async connect(options: ConnectOptions): Promise<void> {
    const gen = ++this.generation;
    await this.teardown();
    // Outra ligação (ou uma saída) começou enquanto se desmontava a anterior:
    // esta já nasceu velha.
    if (gen !== this.generation) return;

    const room = new Room({
      // Só descarrega o vídeo no tamanho que está mesmo a ser mostrado: numa
      // grelha de 9 pessoas é a diferença entre 9 streams HD e 9 miniaturas.
      adaptiveStream: true,
      // Não publica camadas que ninguém está a ver — poupa upload de quem
      // partilha ecrã, que é o caso caro.
      dynacast: true,
      publishDefaults: {
        // DTX corta o débito para ~0 em silêncio; red protege contra perdas.
        dtx: true,
        red: true,
        // Simulcast para o ecrã: quem vê em miniatura não puxa 1080p.
        simulcast: true,
        screenShareEncoding: { maxBitrate: 3_000_000, maxFramerate: 30 },
      },
    });

    this.room = room;
    this.emit({ ...IDLE, state: "connecting", deafened: options.deafened });
    this.wire(room);

    try {
      // Os dois passos vão dentro do MESMO prazo porque é isso que a pessoa vê:
      // o painel diz "A ligar…" desde o primeiro até ao fim do segundo, e é o
      // conjunto que não pode durar para sempre.
      await comPrazo(
        (async () => {
          await room.connect(options.url, options.token);
          // Tentativa abandonada: não se pede o microfone ao browser por uma
          // ligação que já não existe — acendia o LED por uma call que não há.
          if (gen !== this.generation) return;
          await room.localParticipant.setMicrophoneEnabled(options.micEnabled);
        })(),
        MS_LIMITE_DE_LIGACAO,
      );
      if (gen !== this.generation) {
        await drop(room);
        return;
      }
      this.emit({ state: "connected", error: null });
      // Entrei: o "pluc" de confirmação. Já há gesto do utilizador (o clique de
      // entrar), por isso o áudio está destrancado e ouve-se de imediato.
      playSound("join");
      // O medidor arrancava só por evento (`LocalTrackPublished`, `TrackUnmuted`),
      // e um evento perdido — publicado antes do tempo, ou engolido numa
      // reconexão — deixava-o sem arrancar de todo, sem nada que o dissesse.
      // Aqui já se sabe o estado final: pergunta-se em vez de esperar.
      this.medirVoz();
      this.medirRede();
      this.refresh();
    } catch (error) {
      // Tentativa velha a falhar: limpa só a SUA sala e cala-se. Marcar "failed"
      // ou desmontar aqui destruía a ligação actual, que está boa.
      if (gen !== this.generation) {
        await drop(room);
        return;
      }
      // Desmontar PRIMEIRO, marcar a falha DEPOIS: `teardown()` repõe o snapshot
      // em IDLE, por isso a ordem inversa apagava o "failed" no mesmo instante
      // em que o emitia — a barra ficava eternamente em "A ligar…" e o ramo
      // "Sem ligação" da UI nunca chegava a ver-se.
      await this.teardown();
      this.emit({ state: "failed", error: describe(error) });
      throw error;
    }
  }

  /** Sair da call. Invalida qualquer tentativa em curso — nenhuma pode
   *  ressuscitar o motor depois de se sair. */
  async disconnect(): Promise<void> {
    this.generation++;
    await this.teardown();
  }

  /** Desmonta a sala e o áudio sem mexer na geração (é o passo interno que o
   *  `connect` também usa para trocar de sala). */
  private async teardown(): Promise<void> {
    // O medidor primeiro: ele segura um AudioContext, e um contexto por chamada
    // esgota o limite do browser (~6) em seis entradas e saídas.
    this.pararDeMedir();
    this.pararDeMedirRede();
    const room = this.room;
    this.room = undefined;
    if (room) {
      room.removeAllListeners();
      await room.disconnect();
    }
    for (const element of this.attached.values()) element.remove();
    this.attached.clear();
    this.audioSink?.remove();
    this.audioSink = undefined;
    this.snapshot = IDLE;
    // Os pings vão-se com a call: um número da chamada anterior a aparecer na
    // seguinte era uma mentira com ar de dado.
    this.net = {};
    for (const listener of this.netListeners) listener();
    for (const listener of this.listeners) listener();
  }

  // ── controlos ───────────────────────────────────────────────

  async setMic(enabled: boolean): Promise<void> {
    if (!this.room) return;
    if (enabled && !isSecureMediaContext()) {
      this.emit({ micEnabled: false, error: INSECURE_CONTEXT_MSG });
      return;
    }
    try {
      await this.room.localParticipant.setMicrophoneEnabled(enabled);
      this.emit({ micEnabled: enabled, error: null });
    } catch (error) {
      this.emit({ error: describe(error) });
    }
    this.refresh();
  }

  async setCamera(enabled: boolean): Promise<void> {
    if (!this.room) return;
    if (enabled && !isSecureMediaContext()) {
      this.emit({ cameraEnabled: false, error: INSECURE_CONTEXT_MSG });
      return;
    }
    try {
      await this.room.localParticipant.setCameraEnabled(enabled);
      this.emit({ cameraEnabled: enabled, error: null });
    } catch (error) {
      this.emit({ error: describe(error) });
    }
    this.refresh();
  }

  /**
   * `audio: true` partilha também o som do separador — é o que falta em 90%
   * das demos de produto feitas por partilha de ecrã.
   */
  async setScreenShare(enabled: boolean): Promise<void> {
    if (!this.room) return;
    if (enabled && !isSecureMediaContext()) {
      this.emit({ screenEnabled: false, error: INSECURE_CONTEXT_MSG });
      return;
    }
    try {
      await this.room.localParticipant.setScreenShareEnabled(enabled, { audio: true });
      this.emit({ screenEnabled: enabled, error: null });
    } catch (error) {
      // Cancelar o diálogo do browser é uma escolha, não uma avaria.
      const cancelled = error instanceof Error && error.name === "NotAllowedError";
      this.emit({ screenEnabled: false, error: cancelled ? null : describe(error) });
    }
    this.refresh();
  }

  /**
   * Silencia (ou reactiva) o SOM da minha partilha de ecrã sem parar o vídeo —
   * "estou a mostrar, mas não quero que ouçam o que toca aqui". Muta a publicação
   * local do track de áudio do ecrã: o SFU deixa de o encaminhar, ninguém ouve,
   * e a imagem continua a ir na mesma. Sem partilha com áudio não faz nada.
   */
  async setScreenAudioMuted(muted: boolean): Promise<void> {
    const local = this.room?.localParticipant;
    if (!local) return;
    const publication = local.getTrackPublication(Track.Source.ScreenShareAudio);
    if (!publication) return;
    try {
      if (muted) await publication.mute();
      else await publication.unmute();
      this.emit({ error: null });
    } catch (error) {
      this.emit({ error: describe(error) });
    }
    this.refresh();
  }

  setDeafened(deafened: boolean): void {
    this.emit({ deafened });
    if (!this.room) return;
    for (const participant of this.room.remoteParticipants.values()) {
      // Voltar a ouvir é voltar ao volume DELE, não a 100%: quem baixou o
      // colega que fala aos gritos não quer que um clique no ensurdecer lhe
      // desfaça a preferência em silêncio.
      this.applyVolume(participant, this.volumeOf(participant.identity));
    }
  }

  /** O volume a que oiço uma pessoa (0..1). 1 = nunca lhe mexi. */
  getUserVolume(identity: string): number {
    return readUserVolume(identity);
  }

  /**
   * Muda o volume DESSA pessoa, só para mim. Guarda-se antes de aplicar: a
   * preferência tem de sobreviver a sair da call, a um refresh e a uma
   * re-subscrição do track. Devolve o valor efectivamente gravado (cortado
   * a [0,1]).
   */
  setUserVolume(identity: string, volume: number): number {
    const value = writeUserVolume(identity, volume);
    const participant = this.room?.remoteParticipants.get(identity);
    // Ensurdecido, a preferência fica gravada mas não se ouve nada — mexer no
    // cursor não pode furar o ensurdecer.
    if (participant && !this.snapshot.deafened) this.applyVolume(participant, value);
    return value;
  }

  /** O que este participante deve tocar AGORA: o ensurdecer manda em tudo. */
  private volumeOf(identity: string): number {
    return this.snapshot.deafened ? 0 : readUserVolume(identity);
  }

  /**
   * O `setVolume` do LiveKit é por FONTE e guarda o valor num mapa que ele
   * volta a aplicar quando o track chega — por isso isto funciona mesmo antes
   * de a pessoa ligar o microfone. Duas chamadas porque a partilha de ecrã tem
   * áudio próprio: baixar alguém e continuar a ouvir o vídeo dele aos berros
   * não é baixar alguém.
   */
  private applyVolume(participant: RemoteParticipant, volume: number): void {
    participant.setVolume(volume, Track.Source.Microphone);
    participant.setVolume(volume, Track.Source.ScreenShareAudio);
  }

  /** Chamado a partir de um clique: é a única forma de destrancar o autoplay. */
  async unlockAudio(): Promise<void> {
    if (!this.room) return;
    await this.room.startAudio();
    this.emit({ needsAudioUnlock: !this.room.canPlaybackAudio });
  }

  // ── eventos do SDK ──────────────────────────────────────────

  private wire(room: Room): void {
    const refresh = () => this.refresh();

    room
      .on(RoomEvent.ParticipantConnected, () => {
        // Alguém entrou na call: som PRÓPRIO, o `arrive`, e não o `join` do meu
        // clique — este é o evento que tem de me tirar de outra janela, aquele é
        // só a confirmação de uma coisa que eu já sabia. Silêncio se eu estiver
        // ensurdecido: deafen é "não me incomodem", e o mais alto de todos os
        // sons a cada entrada era exactamente contrariá-lo.
        if (!this.snapshot.deafened) playSound("arrive");
        // Quem chega não viu o meu anúncio anterior: se tenho a mão no ar,
        // reenvio-o para a lista dele ficar certa (o resto é idempotente).
        const me = this.localId;
        if (me && this.snapshot.raisedHands.includes(me)) this.sendData({ k: "hand", up: true });
        // Quem chega não está na lista de permissões que já foi enviada — e com
        // o "todos" desligado isso significa NÃO RECEBER NADA meu, nem o ecrã
        // partilhado. Reaplica-se para o incluir.
        if (this.snapshot.privateTo) void this.applyPrivateVoice();
        this.refresh();
      })
      .on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (!this.snapshot.deafened) playSound("leave");
        // Saiu de mão no ar: baixa-a, senão ficava um ✋ pendurado num tile vazio.
        this.setHand(participant.identity, false);
        this.setPrivateFrom(participant.identity, false);
        // Foi-se embora a pessoa a quem eu falava em privado. Sair do modo é
        // obrigatório: ficar em privado para um ausente é ficar MUDO para a sala
        // toda sem nada no ecrã que o explique.
        if (this.snapshot.privateTo === participant.identity) void this.setPrivateVoice(null);
        this.refresh();
      })
      .on(RoomEvent.ActiveDeviceChanged, refresh)
      .on(RoomEvent.MediaDevicesChanged, refresh)
      .on(RoomEvent.TrackPublished, refresh)
      .on(RoomEvent.TrackUnpublished, refresh)
      .on(RoomEvent.LocalTrackPublished, () => {
        this.medirVoz();
        // Um traço novo (câmara, ecrã) nasce sem constar da lista de permissões
        // e por isso nasce invisível para todos menos o alvo. Reaplicar é o que
        // faz a partilha de ecrã aparecer também durante uma voz privada.
        if (this.snapshot.privateTo) void this.applyPrivateVoice();
        this.refresh();
      })
      .on(RoomEvent.LocalTrackUnpublished, () => {
        this.medirVoz();
        this.refresh();
      })
      // Mutar e desmutar são os dois momentos em que o medidor tem de acordar ou
      // calar-se: um microfone mudo continua publicado, e sem isto o anel ficava
      // aceso a medir um track que já não vai para lado nenhum.
      .on(RoomEvent.TrackMuted, () => {
        this.medirVoz();
        this.refresh();
      })
      .on(RoomEvent.TrackUnmuted, () => {
        this.medirVoz();
        this.refresh();
      })
      .on(RoomEvent.ConnectionQualityChanged, refresh)
      .on(RoomEvent.ActiveSpeakersChanged, refresh)
      .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        if (track.kind === Track.Kind.Audio) this.attachAudio(track, participant);
        this.refresh();
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        this.detachAudio(track.sid ?? "");
        this.refresh();
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        this.emit({ needsAudioUnlock: !room.canPlaybackAudio });
      })
      .on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
        if (topic !== DATA_TOPIC) return;
        let data: unknown;
        try {
          data = JSON.parse(dec.decode(payload));
        } catch {
          return;
        }
        const from = participant?.identity ?? "";
        // Mão no ar e reacções são estado/efeito da CALL, não do quadro: trata-se
        // aqui e não se despacha para os ouvintes do canal (o quadro ignorá-los-ia
        // à mesma, mas separar deixa cada um com o seu vocabulário).
        const rec = data as {
          k?: unknown;
          up?: unknown;
          on?: unknown;
          emoji?: unknown;
          rtt?: unknown;
          loss?: unknown;
        };
        if (rec.k === "hand") {
          this.setHand(from, Boolean(rec.up));
          return;
        }
        if (rec.k === "react") {
          this.emitReaction(from, String(rec.emoji ?? ""));
          return;
        }
        if (rec.k === "priv") {
          this.setPrivateFrom(from, Boolean(rec.on));
          return;
        }
        if (rec.k === "net") {
          // O relógio é o NOSSO: carimbar com o `at` que vem na mensagem era
          // pendurar a validade da amostra no relógio da máquina do outro, que
          // pode estar adiantado meia hora.
          this.guardarRede(from, {
            rttMs: typeof rec.rtt === "number" ? rec.rtt : null,
            lossPct: typeof rec.loss === "number" ? rec.loss : null,
            at: Date.now(),
          });
          return;
        }
        for (const listener of this.dataListeners) listener(from, data);
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        if (state === ConnectionState.Reconnecting) this.emit({ state: "reconnecting" });
        else if (state === ConnectionState.Connected) this.emit({ state: "connected", error: null });
      })
      .on(RoomEvent.Disconnected, () => {
        // Queda do lado do servidor (ou um kick): limpar tudo, sem tentar
        // reentrar — quem decide voltar a entrar é o utilizador. Este handler só
        // dispara em quedas ESPONTÂNEAS: a saída à mão remove os listeners antes
        // de `room.disconnect()`, por isso o "leave" do botão é tocado à parte.
        playSound("leave");
        void this.disconnect();
      })
      .on(RoomEvent.MediaDevicesError, (error) => this.emit({ error: describe(error) }));
  }

  private attachAudio(track: Track, participant: Participant): void {
    if (!this.audioSink) {
      // Fora do React de propósito: um re-render não pode arrancar o elemento
      // de áudio do DOM a meio de uma frase.
      const sink = document.createElement("div");
      sink.dataset["gvAudioSink"] = "";
      sink.style.display = "none";
      document.body.append(sink);
      this.audioSink = sink;
    }

    const element = track.attach();
    if (element instanceof HTMLMediaElement) {
      // Sem `webAudioMix`, o `setVolume` do LiveKit acaba aqui — num
      // `element.volume`. Escrevê-lo à mão no momento em que o elemento nasce
      // fecha a janela em que o áudio já toca e o mapa de volumes do SDK ainda
      // não foi reaplicado: sem isto ouve-se meio segundo a 100% de quem foi
      // deliberadamente baixado.
      if (participant instanceof RemoteParticipant) element.volume = this.volumeOf(participant.identity);
      this.audioSink.append(element);
      this.attached.set(track.sid ?? String(this.attached.size), element);
    }
  }

  private detachAudio(sid: string): void {
    const element = this.attached.get(sid);
    if (!element) return;
    element.remove();
    this.attached.delete(sid);
  }

  /**
   * Liga (ou desliga) o medidor de voz ao microfone que está publicado agora.
   *
   * Chamado a cada mexida nas publicações — publicar, mutar, desmutar, sair —,
   * e é idempotente: se já está ligado ao track certo, não faz nada. Sem isso,
   * cada evento do LiveKit abria um `AudioContext` novo, e o browser dá seis.
   *
   * O limiar tem HISTERESE (sobe a 0.08, desce a 0.04): com um só valor, uma voz
   * a passear à volta dele faz o anel piscar dez vezes por segundo — e um anel a
   * piscar lê-se como avaria, não como "estás a falar".
   */
  private medirVoz(): void {
    const room = this.room;
    const publicacao = room?.localParticipant.getTrackPublication(Track.Source.Microphone);
    const track = publicacao && !publicacao.isMuted ? (publicacao.track as LocalAudioTrack | undefined) : undefined;

    if (!track) {
      this.pararDeMedir();
      return;
    }
    if (this.analisador) return;

    try {
      this.analisador = abrirAnalisador(track);
      this.escuta = ESCUTA_INICIAL;
      // O diagnóstico: guarda-se o MAIOR nível visto e, uma vez, ao fim de três
      // segundos, diz-se em voz alta se ele foi zero absoluto. Zero absoluto não
      // é um microfone baixinho — é o analisador a não ler nada, e a única
      // maneira de distinguir "mal afinado" de "surdo" sem estar à frente da
      // máquina. Foram três voltas de afinação às cegas a pagar por esta linha.
      let maximo = 0;
      let porDizer = true;
      const nascido = Date.now();
      this.relogioDaVoz = window.setInterval(() => {
        const nivel = this.analisador?.volume() ?? 0;
        if (nivel > maximo) maximo = nivel;
        if (porDizer && Date.now() - nascido > 3_000) {
          porDizer = false;
          if (maximo === 0) {
            console.warn(
              `[voz] o analisador não leu nada em 3s (contexto ${this.analisador?.suspenso() ? "SUSPENSO" : "a correr"}). ` +
                "Se o microfone está aberto, o problema é o analisador — não o limiar.",
            );
          }
        }
        const antes = this.escuta.aFalar;
        this.escuta = ouvir(nivel, this.escuta);
        if (this.escuta.aFalar !== antes) this.refresh();
      }, PASSO_DA_VOZ_MS);
    } catch {
      // Sem Web Audio (contexto inseguro, browser antigo) fica-se com o sinal do
      // servidor, que é o que havia antes. Um indicador é um extra; falhar em
      // silêncio é o comportamento certo.
      this.analisador = null;
    }
  }

  // ── o estado da ligação (o «anteninho») ─────────────────────

  /**
   * Mede o meu ping e anuncia-o à sala, a cada cinco segundos.
   *
   * O relatório vem de um track — é preciso um `RTCRtpSender` ou `Receiver`
   * para haver estatísticas — e a ordem de procura não é arbitrária: primeiro
   * o MEU microfone, que mede o caminho por onde a minha voz sai, e só depois
   * um track recebido. Entra-se MUDO em todas as calls desta app, portanto
   * quem só está a ouvir não tem sender nenhum publicado, e sem a segunda
   * fonte ficava sem número exactamente na situação mais comum de todas.
   *
   * Sozinho na sala e sem microfone não há nada que meça: fica sem número, e
   * é a resposta honesta — não há caminho nenhum a medir.
   */
  private medirRede(): void {
    this.pararDeMedirRede();
    const uma = () => {
      void this.medirRedeAgora().catch(() => undefined);
    };
    uma();
    this.relogioDaRede = window.setInterval(uma, INTERVALO_MS);
  }

  private async medirRedeAgora(): Promise<void> {
    const room = this.room;
    if (!room) return;

    const meu = room.localParticipant.getTrackPublications().find((p) => p.track)?.track;
    const recebido = [...room.remoteParticipants.values()]
      .flatMap((p) => [...p.trackPublications.values()])
      .find((p) => p.track)?.track;
    // O tipo BASE `Track` não declara `getRTCStatsReport` — quem o tem é o
    // `LocalTrack` e o `RemoteTrack`. Perguntar pelo método em vez de comparar
    // classes evita importar as duas só para um `instanceof`, e continua a ser
    // uma pergunta ao objecto real e não uma promessa ao compilador.
    const alvo = meu ?? recebido;
    const report = temStats(alvo) ? await alvo.getRTCStatsReport() : undefined;
    if (room !== this.room) return;

    const doFluxo = report ? lerRede(report, Date.now()) : null;
    // O CASO NORMAL desta app é não haver track nenhum: entra-se MUDO em todas
    // as calls, e enquanto ninguém abrir o microfone nem ligar a câmara não há
    // sender nem receiver, logo não há `getStats` de onde tirar seja o que for.
    // Era exactamente por isso que o mostrador ficava sem número na situação
    // mais comum de todas — sozinho na sala, ou com toda a gente calada.
    //
    // A ligação, essa, existe sempre: o WebSocket de sinalização troca
    // ping/pong com o mesmo servidor e o SDK guarda esse tempo. Mede outro
    // caminho (sinalização, não media) e por isso é o ÚLTIMO recurso — mas é
    // o mesmo servidor, e um número honesto vale mais do que um espaço vazio.
    const amostra: NetSample =
      doFluxo && doFluxo.rttMs !== null ? doFluxo : { ...(doFluxo ?? SEM_MEDIDA), rttMs: rttDeSinal(room) };

    const eu = room.localParticipant.identity;
    if (eu) this.guardarRede(eu, { ...amostra, at: Date.now() });

    // Anunciar é o que dá ping aos OUTROS ao lado do meu nome. Lossy de
    // propósito: uma medição perdida é substituída pela próxima daqui a cinco
    // segundos, e um número de ligação não vale uma retransmissão.
    if (amostra.rttMs === null && amostra.lossPct === null) return;
    this.sendData({ k: "net", rtt: amostra.rttMs, loss: amostra.lossPct }, false);
  }

  private guardarRede(identity: string, amostra: NetSample): void {
    if (!identity) return;
    this.net = { ...this.net, [identity]: amostra };
    for (const listener of this.netListeners) listener();
  }

  private pararDeMedirRede(): void {
    if (this.relogioDaRede !== null) window.clearInterval(this.relogioDaRede);
    this.relogioDaRede = null;
  }

  private pararDeMedir(): void {
    if (this.relogioDaVoz !== null) window.clearInterval(this.relogioDaVoz);
    this.relogioDaVoz = null;
    void this.analisador?.parar().catch(() => undefined);
    this.analisador = null;
    if (this.escuta.aFalar) {
      this.escuta = ESCUTA_INICIAL;
      this.refresh();
    }
    this.escuta = ESCUTA_INICIAL;
  }

  /** Reconstrói a vista dos participantes e publica-a se mudou de facto. */
  private refresh(): void {
    const room = this.room;
    if (!room) return;

    // O meu `speaking` vem do meu microfone, não do servidor (ver `medirVoz`).
    // O dos outros só pode vir de lá — o áudio deles chega já codificado, e o
    // nível que interessa é o que o SFU mediu à entrada.
    const eu = view(room.localParticipant, true);
    const participants = [
      { ...eu, speaking: eu.speaking || this.escuta.aFalar },
      ...[...room.remoteParticipants.values()].map((p) => view(p, false)),
    ];

    const local = room.localParticipant;
    const screenAudio = local.getTrackPublication(Track.Source.ScreenShareAudio);
    const next: VoiceSnapshot = {
      ...this.snapshot,
      participants,
      micEnabled: local.isMicrophoneEnabled,
      cameraEnabled: local.isCameraEnabled,
      screenEnabled: local.isScreenShareEnabled,
      screenAudioAvailable: !!screenAudio,
      screenAudioMuted: screenAudio ? screenAudio.isMuted : false,
      needsAudioUnlock: !room.canPlaybackAudio,
    };

    if (!changed(this.snapshot, next)) return;
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }
}

/**
 * O analisador do microfone — e porque é NOSSO e não o do SDK.
 *
 * O `createAudioAnalyser` do livekit-client faz exactamente a medição certa
 * (RMS sobre as 1024 bandas da FFT), mas cria um `AudioContext` próprio e
 * NÃO o devolve. E um `AudioContext` nascido fora de um gesto do utilizador
 * nasce SUSPENSO no Chrome: `getByteFrequencyData` escreve zeros, para sempre,
 * com o microfone perfeitamente aberto. O SDK tenta retomá-lo no primeiro
 * clique no `body` e remove o ouvinte a seguir — uma tentativa, e quem não
 * chegar ao `body` (um clique com `stopPropagation` pelo caminho) fica sem
 * ela. Como não há handle, não havia forma de saber se aconteceu nem de
 * insistir: o medidor podia estar surdo e parecer apenas mal afinado.
 *
 * Aqui o contexto é nosso. Retoma-se à cabeça e volta a tentar-se a cada
 * leitura enquanto estiver suspenso — é uma comparação de string por décimo de
 * segundo, e resolve-se sozinho no primeiro gesto que o browser aceitar, sem
 * depender de apanhar o clique certo.
 *
 * A janela em decibéis são os valores do SDK, e são-no por uma razão: mede-se
 * banda a banda, e a energia de uma voz espalha-se pelas 1024 — o nível POR
 * BANDA fica muito abaixo do nível total. Uma janela `-70/-20`, que parece a
 * gama da voz humana, mapeia quase toda a fala para zero.
 */
function abrirAnalisador(track: LocalAudioTrack): Analisador {
  const Contexto = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Contexto) throw new Error("sem Web Audio");

  const contexto = new Contexto({ latencyHint: "interactive" });
  void contexto.resume().catch(() => undefined);

  const fonte = contexto.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
  const analisador = contexto.createAnalyser();
  analisador.fftSize = 2048;
  analisador.smoothingTimeConstant = 0.4;
  analisador.minDecibels = -100;
  analisador.maxDecibels = -80;
  fonte.connect(analisador);

  const bandas = new Uint8Array(analisador.frequencyBinCount);
  return {
    volume: () => {
      if (contexto.state === "suspended") {
        void contexto.resume().catch(() => undefined);
        return 0;
      }
      analisador.getByteFrequencyData(bandas);
      let soma = 0;
      for (const amplitude of bandas) soma += (amplitude / 255) ** 2;
      return Math.sqrt(soma / bandas.length);
    },
    suspenso: () => contexto.state === "suspended",
    parar: () => contexto.close().catch(() => undefined),
  };
}

interface Analisador {
  volume: () => number;
  /** Para o diagnóstico: um contexto suspenso mede zeros e não é avaria do microfone. */
  suspenso: () => boolean;
  parar: () => Promise<void>;
}

/** De quanto em quanto tempo se mede. 100ms = 10 leituras por segundo: mais
 *  depressa não se vê, mais devagar sente-se o atraso entre falar e acender. */
const PASSO_DA_VOZ_MS = 100;

/** Quanto o nível tem de subir ACIMA do ruído de fundo para contar como voz. */
const SALTO = 2.5;
/** O mínimo absoluto — numa sala em silêncio total o piso é ~0, e sem este
 *  chão qualquer sopro acendia o anel. */
const LIMIAR_MINIMO = 0.015;
/** Quanto o piso SOBE por leitura. A 10 leituras por segundo, 0.002 leva ~2,5s
 *  a engolir uma ventoinha que arrancou — devagar de propósito: se subisse
 *  depressa, engolia também a voz. Descer é instantâneo (ver `ouvir`). */
const SUBIDA_DO_PISO = 0.002;

/** O que se sabe entre duas leituras: se estava a falar, e onde está o silêncio. */
export interface Escuta {
  aFalar: boolean;
  /** O nível do ruído de fundo desta pessoa, nesta sala, agora. */
  piso: number;
}

export const ESCUTA_INICIAL: Escuta = { aFalar: false, piso: 0 };

/**
 * Estou a falar? — sem nenhum número escolhido às cegas.
 *
 * A primeira versão tinha dois limiares FIXOS, e foi um erro que se pagou no
 * ecrã: escolhi-os sem poder ouvir o resultado, ficaram altos de mais, e a
 * pessoa falava com o microfone comprovadamente aberto (o SFU dizia
 * `muted=false`) sem o anel acender uma única vez. Um limiar fixo é uma aposta
 * sobre o microfone, o ganho e a sala de quem está do outro lado — três coisas
 * que eu não conheço.
 *
 * Aqui não há aposta: o PISO aprende sozinho onde está o silêncio desta pessoa,
 * e a voz é o que salta `SALTO` vezes acima dele. Num portátil baixinho o piso
 * é 0.01 e basta 0.025; numa sala com ventoinha o piso sobe e o limiar sobe com
 * ele, sem apanhar a ventoinha.
 *
 * O piso só aprende em SILÊNCIO — a falar, ele subiria até à voz e o anel
 * apagava-se sozinho a meio da frase. E a histerese fica: acender custa mais do
 * que continuar aceso, senão as pausas entre sílabas fazem-no piscar dez vezes
 * por segundo, e um anel a piscar lê-se como avaria.
 */
export function ouvir(nivel: number, antes: Escuta): Escuta {
  // O piso segue o MÍNIMO recente: desce de imediato para qualquer nível mais
  // baixo (o silêncio entre palavras diz-lhe onde está o silêncio) e sobe devagar
  // (uma ventoinha que arranca acaba por ser piso; uma frase não).
  //
  // A primeira versão congelava o piso enquanto se falava, e o teste apanhou o
  // buraco: um ruído súbito acendia o anel ANTES de o piso ter aprendido, e a
  // partir daí o piso deixava de aprender — o anel ficava preso aceso para
  // sempre. Com o mínimo não há nada para congelar, e o problema desaparece em
  // vez de ser remendado.
  const piso = Math.min(nivel, antes.piso + SUBIDA_DO_PISO);
  const limiar = Math.max(LIMIAR_MINIMO, piso * SALTO);
  const aFalar = antes.aFalar ? nivel > limiar * 0.6 : nivel > limiar;
  return { aFalar, piso };
}

/** A vista de UMA pessoa. Exportada para teste porque decide a coisa mais visível
 *  do palco: haver imagem ou haver avatar. */
export function view(participant: Participant, isLocal: boolean): ParticipantView {
  let camera: Track | null = null;
  let screen: Track | null = null;
  let micEnabled = false;

  for (const publication of participant.trackPublications.values() as Iterable<TrackPublication>) {
    // MUDO = não há imagem, e portanto não há track para o palco. Quem manda é o
    // `isMuted`, nunca a existência da publicação: desligar a câmara no LiveKit
    // MUTA (só a partilha de ecrã é despublicada), a publicação fica de pé com o
    // `track` lá dentro, e o dispositivo é largado — o `<video>` continuava
    // agarrado a um track já morto e o tile ficava um RECTÂNGULO PRETO por cima
    // do avatar, para sempre. Vale para os dois lados: um colega que desliga a
    // câmara dele muta do lado dele e chega cá igual.
    const imagem = publication.isMuted ? null : (publication.track ?? null);
    if (publication.source === Track.Source.Camera) camera = imagem;
    else if (publication.source === Track.Source.ScreenShare) screen = imagem;
    else if (publication.source === Track.Source.Microphone) micEnabled = !publication.isMuted;
  }

  return {
    identity: participant.identity,
    isLocal,
    speaking: participant.isSpeaking,
    micEnabled,
    quality: participant.connectionQuality,
    camera,
    screen,
  };
}

/** Comparação superficial do que a UI mostra — evita re-render por nada.
 *  Exportada para teste: é o coração do modelo de performance (todo o snapshot
 *  só chega ao React se isto disser `true`), por isso é o que mais vale trancar. */
export function changed(a: VoiceSnapshot, b: VoiceSnapshot): boolean {
  if (
    a.state !== b.state ||
    a.error !== b.error ||
    a.micEnabled !== b.micEnabled ||
    a.cameraEnabled !== b.cameraEnabled ||
    a.screenEnabled !== b.screenEnabled ||
    a.screenAudioAvailable !== b.screenAudioAvailable ||
    a.screenAudioMuted !== b.screenAudioMuted ||
    a.deafened !== b.deafened ||
    a.needsAudioUnlock !== b.needsAudioUnlock ||
    a.privateTo !== b.privateTo ||
    a.privateFrom.length !== b.privateFrom.length ||
    a.privateFrom.some((identity, i) => identity !== b.privateFrom[i]) ||
    a.participants.length !== b.participants.length
  ) {
    return true;
  }

  for (let i = 0; i < a.participants.length; i += 1) {
    const x = a.participants[i]!;
    const y = b.participants[i]!;
    if (
      x.identity !== y.identity ||
      x.speaking !== y.speaking ||
      x.micEnabled !== y.micEnabled ||
      x.quality !== y.quality ||
      x.camera !== y.camera ||
      x.screen !== y.screen
    ) {
      return true;
    }
  }
  return false;
}

/** Deita fora uma sala órfã (de uma tentativa de ligação que deixou de ser a
 *  actual) sem tocar no estado do motor. Silencioso de propósito: se a própria
 *  desmontagem falhar não há nada a dizer a ninguém — a sala já não conta. */
/**
 * A mesma promessa, mas com hora para acabar.
 *
 * O `then` de dois braços não é estilo: é o que garante que a promessa lenta
 * fica com quem a trate quando ela finalmente responder — sem isso, a rejeição
 * que chega depois do prazo aparece como "unhandled rejection" na consola de
 * quem estiver a usar o programa.
 */
function comPrazo<T>(promessa: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((cumprir, falhar) => {
    const relogio = setTimeout(() => falhar(new Error(`A ligação não respondeu em ${Math.round(ms / 1000)}s.`)), ms);
    promessa.then(
      (valor) => {
        clearTimeout(relogio);
        cumprir(valor);
      },
      (erro: unknown) => {
        clearTimeout(relogio);
        falhar(erro);
      },
    );
  });
}

async function drop(room: Room): Promise<void> {
  room.removeAllListeners();
  try {
    await room.disconnect();
  } catch {
    /* sala já morta */
  }
}

function describe(error: unknown): string {
  if (!(error instanceof Error)) return "Falha desconhecida no áudio/vídeo.";
  // Sem contexto seguro, `navigator.mediaDevices` é `undefined` e o SDK rebenta
  // com um TypeError cru ("Cannot read properties of undefined") — traduz-se.
  if (error instanceof TypeError && /mediaDevices|getDisplayMedia|getUserMedia/i.test(error.message)) {
    return INSECURE_CONTEXT_MSG;
  }
  switch (error.name) {
    case "NotAllowedError":
      return "Permissão negada. Autorize o microfone/tela nas configurações do browser.";
    case "NotFoundError":
      return "Nenhum microfone ou câmera encontrados.";
    case "NotReadableError":
      return "O dispositivo está sendo usado por outro aplicativo.";
    default:
      return error.message;
  }
}

/** Uma sala por aba — participar em duas ao mesmo tempo não faz sentido. */
export const voiceEngine = new VoiceEngine();
